import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { Search as SearchPatientIcon } from "@mui/icons-material";
import { Box, Grid, Paper, Typography, Button} from '@mui/material';
import Form  from '../../components/general/form';
import {useHistory, useParams} from 'react-router-dom';
import Loader from '../../components/Loader';
import PatientsTable from '../../components/general/PatientsTable';
import { ButtonBack, ButtonCancel, ButtonContinue, GridContainer } from '../../components/general/mini_components';
import { connect } from 'react-redux';
import { useDispatch } from "react-redux";
import { incrementalSyncPatientsAction, setPatientSearchAction, clearPatientSearchAction } from '../../redux/actions/patientsActions';
import _ from 'lodash';
import Modal from '../../components/general/modal';
import { DepartmentsAccordionRedux } from './departments/DepartmentsAccordion';
import { decryptSinglePatientData, getPatientID, yearsFromDate } from '../../utils/index.jsx';
import ICT from '../../components/general/SmartFields/ICT';
import { searchPatientByDiagnosis } from '../../services';
import PatientAppointmentInfo from './Outpatients/PatientAppointmentInfo';
import { HOSPITAL_BILLING_PATIENT, HOSPITAL_PATIENT } from '../../routes/urls';
import { TabsSherwood } from '../components/Tabs';
import RequestForm from './Request/RequestForm';
import { ServiceType } from './Request/types';
import { IPatient } from '../../constants/types';
import PatientInfo from '../../components/PatientInfo';
import { fetchPatient } from '../../db';
import { GROUPED_INVESTIGATION_UUID } from '../../constants/grouped';
import { SELECT_INVESTIGATION } from '../../constants';

let personalFieldsForm = {};
const ID_FIELD = {
    name : "id",
    required: false,
    type : "text",
    label : "id",
    order: 0
}

// Fixed column set for the grouped ("All my investigations") patient list, since
// personalFields differ per investigation.
const GROUPED_COLUMNS = [
    { name: "name", required: false, type: "text", label: "Name", order: 0 },
    { name: "surnames", required: false, type: "text", label: "Surnames", order: 1 },
    { name: "investigationName", required: false, type: "text", label: "Investigation", order: 2 },
]

function SearchPatients(props: any){
    // Last search persisted in Redux — restored here so results survive the
    // component unmounting when the user drills into a patient and goes back.
    const savedSearch = props.patients.search;
    const [valuesSearch, setValuesSearch] = useState(savedSearch ? savedSearch.valuesSearch : null);
    const [showResults, setShowResults] = useState(savedSearch ? savedSearch.showResults : false);
    const [filteredPatients, setFilteredPatients] = useState<IPatient[]>(savedSearch ? savedSearch.filteredPatients : []);
    const [patientsDiagnoseLoading, setPatientsDiagnoseLoading] = useState(false);
    const insurances = props.investigations.currentInvestigation ? props.investigations.currentInvestigation.insurances : [];
    const coverages = props.investigations.currentInvestigation ? props.investigations.currentInvestigation.coverages : [];
    const outpatientsInfo = props.investigations.currentInvestigation ? props.investigations.currentInvestigation.outpatientsInfo : {};
    const grouped = Boolean(props.investigations.groupedMode);
    // In grouped mode the patient list is the aggregated bucket under the sentinel key;
    // otherwise it is the current investigation's own bucket.
    const patientsListUuid = grouped
        ? GROUPED_INVESTIGATION_UUID
        : (props.investigations.currentInvestigation ? props.investigations.currentInvestigation.uuid : null);
    const patients = props.patients.data && patientsListUuid ? (props.patients.data[patientsListUuid] || []) : [];
    const [actionPatient, setActionPatient] = useState<PatientAction | null>(null);
    const dispatch = useDispatch();
    const history = useHistory();
    const { action, uuidPatient } = useParams();
    const [selectedPatient, setSelectedPatient] = useState<IPatient | null>(null);
    const [filtering, setFiltering] = useState(false);
    const workerRef = useRef<Worker | null>(null);
    // Page number persisted in the URL (?page=N) so that drilling into a patient
    // and navigating Back restores the page the user was on. Only the non-sensitive
    // page index goes in the URL — never the (encrypted) search terms, which are
    // kept in Redux (props.patients.search) to honour the zero-knowledge model.
    const [currentPage, setCurrentPage] = useState<number>(() => {
        const raw = new URLSearchParams(history.location.search).get('page');
        const parsed = raw ? parseInt(raw, 10) : 0;
        return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
    });

    function writePageToUrl(newPage: number){
        const params = new URLSearchParams(history.location.search);
        if (newPage > 0) {
            params.set('page', String(newPage));
        } else {
            params.delete('page');
        }
        const search = params.toString();
        history.replace({ pathname: history.location.pathname, search: search ? `?${search}` : '' });
    }

    function changePageCallback(newPage: number){
        setCurrentPage(newPage);
        writePageToUrl(newPage);
    }

    function resetPage(){
        setCurrentPage(0);
        writePageToUrl(0);
    }
    // When a personal-details search was restored from Redux, skip the one
    // mount-time run of the filtering effect below: results are already restored,
    // re-posting to the worker would only flash the loader.
    const skipNextFilter = useRef<boolean>(Boolean(savedSearch && savedSearch.valuesSearch != null));

    // Initialize Web Worker for patient filtering (off main thread)
    useEffect(() => {
        workerRef.current = new Worker(
            new URL('../../workers/patientFilter.worker.ts', import.meta.url),
            { type: 'module' }
        );
        workerRef.current.onmessage = (e) => {
            if (e.data.type === 'results') {
                setFilteredPatients(e.data.patients);
                setShowResults(true);
                setFiltering(false);
            }
        };
        return () => {
            workerRef.current?.terminate();
        };
    }, []);

    async function searchPatientCallBack(values: any) {
        console.log(values);
        resetPage();
        setValuesSearch(values);
        
        // Fetch any updated patients using event-based incremental sync (faster)
        // Note: silent = false triggers loading state, which then triggers the useEffect for filtering
        await dispatch(incrementalSyncPatientsAction(props.investigations.currentInvestigation.uuid, false)); 
        
    }
    function diagnoseSelectedCallBack(icd: any) {
        resetPage();
        setPatientsDiagnoseLoading(true);
        searchPatientByDiagnosis(props.investigations.currentInvestigation.uuid, icd["icd-code"])
        .then(async response => {
            setShowResults(true);
            const decryptedPatients = await Promise.all(response.patients.map(async (patient: IPatient) => {
                patient.personalData = await decryptSinglePatientData(patient.personalData, props.investigations.currentInvestigation);
                return patient;
            }));
            setFilteredPatients(decryptedPatients);
            setPatientsDiagnoseLoading(false);
        })
        .catch(error =>{
            console.error(error);
            setPatientsDiagnoseLoading(false);
        })
    }
    function backToSearchCallBack() {
        setFilteredPatients([]);
        setShowResults(false);
        dispatch(clearPatientSearchAction());
        resetPage();
    }
    
    async function patientSelectedCallBack(uuid: string) {
        if(grouped){
            // Drill-in: point currentInvestigation at the patient's real investigation
            // so the detail screen and all its hooks operate on a real investigation.
            // keepGrouped keeps groupedMode on, so navigating Back returns to the
            // aggregated list.
            const patient = patients.find((p: any) => p.uuid === uuid);
            if(patient && patient.uuidInvestigation){
                dispatch({ type: SELECT_INVESTIGATION, selectedInvestigation: patient.uuidInvestigation, keepGrouped: true });
                localStorage.setItem("uuidInvestigation", patient.uuidInvestigation);
            }
        }
        const nextUrl = HOSPITAL_PATIENT.replace(":uuidPatient", uuid)
        history.push(nextUrl);
    }

    async function billingPatientCallBack(uuid: string) {
        const nextUrl = HOSPITAL_BILLING_PATIENT.replace(":uuidPatient", uuid)
        history.push(nextUrl);
    }

    
    // Dispatch filtering to Web Worker when sync completes
    useEffect(() => {
        if (skipNextFilter.current) {
            // Mount run after restoring a search from Redux — results already restored.
            skipNextFilter.current = false;
            return;
        }
        if (!props.patients.loading && valuesSearch !== null && workerRef.current) {
            setFiltering(true);
            workerRef.current.postMessage({
                type: 'filter',
                patients,
                searchValues: valuesSearch,
                personalFields: props.investigations.currentInvestigation.personalFields,
            });
        }
    }, [props.patients.loading])

    // Persist the search (criteria + results) to Redux whenever results are shown,
    // so navigating back from a patient detail restores them.
    useEffect(() => {
        if (!showResults) {
            return;
        }
        dispatch(setPatientSearchAction({ valuesSearch, showResults, filteredPatients }));
    }, [showResults, filteredPatients])

    // Handle URL parameters
    useEffect(() => {
        if (uuidPatient && patients.length > 0) {
            const patient = patients.find(p => p.uuid === uuidPatient);
            if (patient) {
                setFilteredPatients([patient]);
                setShowResults(true);
                
                if (action) {
                    setActionPatient({action: action, patient: patient});
                }
            }
        }
    }, [uuidPatient, patients, action]);

    if(!props.investigations.data || !props.investigations.currentInvestigation || props.patients.loading || patientsDiagnoseLoading || filtering){
        // Show progress if available
        const progress = props.patients.progress;
        if (progress) {
            return (
                <Loader 
                    infoString={`Loading patients... ${progress.loaded.toLocaleString()} / ${progress.total.toLocaleString()} (${progress.percentage}%)`}
                />
            );
        }
        return <Loader infoString="Loading patients..." />;
    }
    return(
        <React.Fragment>
            <SearchPatientsComponent showResults={grouped ? true : showResults}
                patients={grouped ? patients : filteredPatients} investigation={props.investigations.currentInvestigation}
                personalFields={grouped ? GROUPED_COLUMNS : props.investigations.currentInvestigation.personalFields}
                permissions={props.investigations.currentInvestigation.permissions}
                functionalities={props.investigations.currentInvestigation.functionalities}
                grouped={grouped}
                insurances={insurances} coverages={coverages} outpatientsInfo={outpatientsInfo}
                initActionPatient={actionPatient}
                currentPage={currentPage}
                changePageCallback={changePageCallback}
                searchPatientCallBack={searchPatientCallBack}
                backToSearchCallBack={backToSearchCallBack}
                patientSelectedCallBack={patientSelectedCallBack}
                billingPatientCallBack={billingPatientCallBack}
                diagnoseSelectedCallBack={diagnoseSelectedCallBack}
                    />
        </React.Fragment>                          
    )
}

const mapStateToProps = (state) =>{
    return {
        patients : state.patients,
        investigations:state.investigations
    }
}

SearchPatients.propTypes = {
    personalFields:PropTypes.array,
}

export default connect(mapStateToProps, null)(SearchPatients)

type PatientAction = {
    action: 'appointment' | 'pre-hospitalize' | 'hospitalize' | 'surgery' | string; // string for future actions
    patient: IPatient;
  }

interface SearchPatientsProps extends LocalizeContextProps {
    showResults: boolean;
    patients: IPatient[];
    investigation: any;
    personalFields: any[];
    permissions: any[];
    functionalities: any[];
    grouped: boolean;
    insurances: any[];
    coverages: any[];
    outpatientsInfo: any;
    initActionPatient: PatientAction | null;
    currentPage: number;
    changePageCallback: (page: number) => void;
    searchPatientCallBack: (values: any) => void;
    backToSearchCallBack: () => void;
    patientSelectedCallBack: (uuid: string) => void;  
    diagnoseSelectedCallBack: (icd: any) => void;
    billingPatientCallBack: (uuid: string) => void;
}


export const SearchPatientsComponent = withLocalize((props: SearchPatientsProps) => {
    const [actionPatient, setActionPatient] = useState<PatientAction | null>(props.initActionPatient);


    useEffect(() => {
        setActionPatient(props.initActionPatient);
    }, [props.initActionPatient]);

    function backToSearch(){
        props.backToSearchCallBack()
    }

    async function hospitalizePatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            setActionPatient({patient: fetchedPatient, action : "pre-hospitalize"});
        }
    }
    async function billingPatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            props.billingPatientCallBack(fetchedPatient.uuid);
        }
    }

    async function selectAppointmentPatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            setActionPatient({action: "appointment", patient: fetchedPatient});
        }
    }

    async function surgeryPatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            setActionPatient({action: "surgery", patient: fetchedPatient});
        }
    }
    
    function confirm(){
        setActionPatient({...actionPatient, action: "hospitalize"});
    }
    function resetModal(){
        setActionPatient(null);
    }

    function renderModal(){
        if(actionPatient?.patient){
            return (
                <Modal key="modal" open={Boolean(actionPatient.patient)} 
                    title={<Translate id={`hospital.search_patient.modal.${actionPatient.action}.title`}/>} 
                    closeModal={resetModal} >
                            <>
                            {
                            (actionPatient && actionPatient.action === 'pre-hospitalize')&&
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography variant="body1">
                                        <Translate id="hospital.search_patient.modal.pre-hospitalize.body" />

                                    </Typography>
                                    <PatientInfo uuidPatient={actionPatient.patient.uuid} />
                                </Grid>
                                <Grid item xs={12} style={{paddingTop:'1rem'}}>
                                    <ButtonCancel onClick={resetModal} data-testid="cancel-modal" color="primary" spaceright={1}>
                                        <Translate id="general.cancel" />
                                    </ButtonCancel>
                                    &nbsp;
                                    <ButtonContinue onClick={confirm} data-testid="continue-modal" color="green">
                                        <Translate id="general.continue" />
                                    </ButtonContinue>
                                </Grid>
                                
                            </Grid>
                        }
                        {
                            actionPatient && actionPatient.action === "hospitalize" &&
                            <DepartmentsAccordionRedux currentInvestigation={props.investigation} 
                                
                                uuidPatient={actionPatient.patient.uuid} />
                        }
                        {
                            actionPatient && actionPatient.action === "appointment" &&
                            <PatientAppointmentInfo uuidInvestigation={props.investigation.uuid}  uuidPatient={actionPatient.patient.uuid} 
                                outpatientsInfo={props.outpatientsInfo} phoneNumber={ Array(props.outpatientsInfo.sms) ? actionPatient.patient.personalData.phone : ""}
                                resetModal={resetModal}
                                appointmentMadeCallback={resetModal} /> 
                        }
                        {
                            actionPatient && actionPatient.action === "surgery" &&
                            <RequestForm uuidInvestigation={props.investigation.uuid}  uuidPatient={actionPatient.patient.uuid} 
                                serviceType={ServiceType.SURGERY} cancel={resetModal}
                                units={[]}
                                callBackRequestFinished={resetModal}/> 
                        }
                        </>
                </Modal>
            )   
        }
        else{
            return null;
        }
        
    }

    function renderCore(){
        if(!props.showResults){
            let personalFieldsForm = {}
            props.personalFields
                .filter((p: any) => p.type !== 'hidden')
                .sort((a,b) => a.order - b.order).forEach(personalField => {
                            const copyField = {...personalField};
                            copyField.required = false;
                            personalFieldsForm[personalField.name] = copyField
                        });
            if(Object.keys(personalFieldsForm).length === 0){
                personalFieldsForm["id"] = ID_FIELD;
            }      
            
            return (
                <TabsSherwood initTab={0} name="Search Patients" 
                    labels={[<Translate id="pages.hospital.search-patient.by-personal-details" />, <Translate id="pages.hospital.search-patient.by-diagnose" />]} >
                    <Box label={<Translate id="pages.hospital.search-patient.by-personal-details" />}>
                        <Paper style={{padding:'1rem', margin:'1rem'}}>
                            <Translate id="pages.hospital.search-patient.note" />
                            <Form fields={personalFieldsForm} 
                                submitText="investigation.search-patients.search" callBackForm={props.searchPatientCallBack} />                        
                        </Paper>
                    </Box>
                    <Box label={<Translate id="pages.hospital.search-patient.by-diagnose" />}>
                        <Paper style={{padding:'1rem', margin:'1rem'}}>
                            <ICT language={props.activeLanguage.code} elementSelected={props.diagnoseSelectedCallBack}/>
                        </Paper>
                    </Box>
                </TabsSherwood> 
            );
        }
        else{
            if(props.patients.length === 0){
                return ([
                    <Box mb={2}>
                        <Paper style={{padding:'1rem'}}>
                            No patients meet the criteria
                        </Paper>
                    </Box>,
                    props.grouped ? null : <ButtonBack mt={2} onClick={backToSearch}><Translate id="pages.hospital.search-patient.back-button" /></ButtonBack>,
                ])
            }
            else{
                let formSearch = [...props.personalFields].filter((p: any) => p.type !== 'hidden')
                if(formSearch.length === 0){
                    formSearch.push(ID_FIELD);
                } 
                console.log("Patients Results", props.patients);
                return(
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <PatientsTable patients={props.patients}
                                personalFields={formSearch} permissions={props.permissions}
                                functionalities={props.functionalities}
                                insurances={props.insurances ? props.insurances : []}
                                coverages={props.coverages ? props.coverages : []}
                                currentPage={props.currentPage}
                                changePageCallback={props.changePageCallback}
                                showPatientCallBack={id => props.patientSelectedCallBack(id)}
                                selectAppointmentPatientCallBack={(index) => selectAppointmentPatient(index)}
                                billingPatientCallBack={(index) => billingPatient(index)}
                                hospitalizePatientCallBack={(index) => hospitalizePatient(index)} 
                                surgeryPatientCallBack={(index) => surgeryPatient(index)}
                                />
                        </Grid>
                        {
                            !props.grouped &&
                            <Grid item xs={12}>
                                <ButtonBack onClick={backToSearch}><Translate id="pages.hospital.search-patient.back-button" /></ButtonBack>
                            </Grid>
                        }
                    </Grid>)
            }
        }
    }
   
    
    return (
        <React.Fragment>
            {
                renderModal()
            }
            <Grid container spacing={2} >
                <GridContainer item xs={12}>
                    <SearchPatientIcon style={{fontSize:"2.5rem"}} />
                    <Typography variant="h1" gutterBottom display="inline" style={{marginBottom:"0px"}}>
                        <Translate id="pages.hospital.search-patient.title" />
                    </Typography>
                </GridContainer>
                
                <Grid item xs={12}>
                    {
                        renderCore()
                    }     
                </Grid>         
            </Grid>
        </React.Fragment>
    )
})